import React from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import { ReactComponent as TickWithBorderIcon } from "../../assets/images/icons/tick-with-border.svg";
import { formatCurrency } from "../../utils/utils";

export const DojoMiniInvoiceWidget = ({
  orderData,
  tableNumber,
  primaryBgColor = null,
  primaryFontColor = null,
  secondaryBgColor = null,
  secondaryFontColor = null,
  tickIconBgColor = null,
  paymentStatus,
  orderUpdationError = false,
  restaurantName
}) => {
  const paymentGateway = orderData?.transactionGateway;
  const isDirectPayment = paymentGateway === 'direct';
  const isSuccess = (paymentStatus === "PAID" || (isDirectPayment && paymentStatus === "AWAITING_PAYMENT_COLLECTION")) && !orderUpdationError;
  const errorColor = "#d32f2f";
  const errorFontColor = "#ffebee";
  const cardTextColor = isSuccess ? isDirectPayment ? "white" : primaryFontColor ?? "#212529" : errorFontColor;
  const cardSubTextColor = isSuccess ? isDirectPayment ? "#19222a" : primaryFontColor ?? "#212529" : errorFontColor;

  // Determine message based on error type
  const getErrorMessage = () => {
    if (orderUpdationError) {
      return "There was a problem updating your order";
    }
    return "There was a problem with your payment";
  };

  return (
    <Box
      sx={{
        ...styles.miniInvoiceWidget,
        backgroundColor: isSuccess ? isDirectPayment ? "#5A8FBB" : primaryBgColor ?? "#95E7BB" : errorColor,
      }}
    >
      <Box
        sx={{
          ...styles.topView,
          "#tick-circle-path": {
            fill: isDirectPayment ? "#5A8FBB" : primaryBgColor ?? "#4ed4a8",
            stroke: tickIconBgColor ?? "#FFF",
          },
          "#tick-path": { fill: tickIconBgColor ?? "#FFF" },
        }}
      >
        {isSuccess && <TickWithBorderIcon style={styles.tickIcon} />}
        <Typography sx={{ mt: "2.5rem", fontSize: "1.25rem", color: cardTextColor }}>
          {restaurantName}
        </Typography>

        <Typography
          mt={2}
          sx={{
            ...styles.thankyouText,
            fontSize: isSuccess ? 24 : 16,
            color: cardTextColor,
          }}
        >
          {isSuccess
            ? "Thank you for your order!"
            : getErrorMessage()}
        </Typography>
        <Typography
          sx={{
            ...styles.orderPlacedText,
            color: cardTextColor,
          }}
        >
          {isSuccess
            ? `Your order #${orderData?.orderRef ?? orderData?._id?.toString().substr(-5)
            } has been placed.\u200e`
            : `Order #${orderData?.orderRef ?? orderData?._id?.toString().substr(-5)
            } - Status: ${orderUpdationError ? "Failed to update" : paymentStatus}`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          gap: 2,
          mt: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              ...styles.defListLabel,
              color: cardSubTextColor,
            }}
          >
            x{orderData?.cart?.length}
          </Typography>
          <Typography
            sx={{
              ...styles.defListValue,
              color: cardTextColor,
              fontWeight: isSuccess ? 400 : 700, // bold for error
              opacity: isSuccess ? isDirectPayment ? 1 : 0.5 : 1,
            }}
          >
            Items ordered
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              ...styles.defListLabel,
              color: cardSubTextColor,
              fontSize: 24,
            }}
          >
            {tableNumber}
          </Typography>
          <Typography
            sx={{
              ...styles.defListValue,
              color: cardTextColor,
              fontSize: 14,
              fontWeight: isSuccess ? 400 : 700, // bold for error
              opacity: isSuccess ? isDirectPayment ? 1 : 0.5 : 1,
            }}
          >
            Table No
          </Typography>
        </Box>

        <Box>
          <Typography
            sx={{
              ...styles.defListLabel,
              color: cardSubTextColor,
            }}
          >
            {formatCurrency(orderData?.remainingToPay || orderData?.totalAmount)}
          </Typography>
          <Typography
            sx={{
              ...styles.defListValue,
              color: cardTextColor,
              fontWeight: isSuccess ? 400 : 700, // bold for error
              opacity: isSuccess ? isDirectPayment ? 1 : 0.5 : 1,
            }}
          >
            Total
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          height: "43px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: isSuccess
            ? isDirectPayment ? "#1e2e3c" : secondaryBgColor ?? "#B0EDCD"
            : errorColor,
          borderBottomRightRadius: "15px",
          borderBottomLeftRadius: "15px",
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Typography
          sx={{
            ...styles.timeText,
            color: isSuccess ? isDirectPayment ? "white" : secondaryFontColor ?? "#595959" : errorFontColor,
          }}
        >
          Order placed at:{" "}
          {moment(orderData?.createdAt).format("hh:mm A   MMM D")}
        </Typography>
      </Box>
    </Box>
  );
};

export default DojoMiniInvoiceWidget;

const styles = {
  miniInvoiceWidget: {
    borderRadius: "15px",
    mx: 1,
    mt: 6,
    height: "270px",
    position: "relative",
  },
  topView: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  tickIcon: {
    position: "absolute",
    top: "-30px",
  },
  thankyouText: {
    fontSize: 24,
    fontFamily: "Inter",
    fontWeight: 700,
    textAlign: "center",
    margin: "4px 0 8px 0",
    padding: 0,
  },
  orderPlacedText: {
    fontSize: 16,
    fontFamily: "Inter",
    fontWeight: 400,
    textAlign: "center",
    margin: 0,
    padding: 0,
  },
  defListLabel: {
    fontFamily: "Inter",
    fontSize: 20,
    fontWeight: 700,
    textAlign: "center",
  },
  defListValue: {
    fontFamily: "Inter",
    fontSize: 11,
    fontWeight: 400,
    opacity: 0.5,
    textAlign: "center",
  },
  sendEmailText: {
    color: "#212529",
    fontSize: 14,
    fontFamily: "GilroySemiBold",
    textAlign: "center",
  },
  timeText: {
    fontSize: 12,
    fontFamily: "Inter",
    fontWeight: 400,
    textAlign: "center",
  },
};
